import React, { useCallback, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Grid,
  Popover,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import {
  useListingField,
  useMediaResource,
  useSyncedMediaResource,
} from '@brightmls/nextgen.lae.flc';
import { useService } from '../../../contexts/ServiceContext';
import { ListingResource, PropertyField } from '../../../system/field';
import TourTile from './TourTile';
import { MediaCategory, MediaType } from '../../../system/media';
import { useMediaValidation } from './hooks/useMediaValidation';
import { MediaMetadata, useMediaUpload } from './hooks/useMediaUpload';
import UploadDialog from './UploadDialog';
import { useUrlValidation } from './hooks/useUrlValidation';
import { useMediaLogger } from './hooks/useMediaLogger';
import BulkDeleteConfirmModal from './BulkDeleteConfirmModal';
import { generateDataTestId } from 'src/utils';

const AddVirtualTour: React.FC = () => {
  const logger = useMediaLogger('AddVirtualTour');
  const theme = useTheme();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const isSmallDisplay = useMediaQuery(theme.breakpoints.down('md'));

  const listingKeyField = useListingField(
    ListingResource.Property,
    PropertyField.LISTING_KEY,
    '',
  );
  const propertyTypeField = useListingField(
    ListingResource.Property,
    PropertyField.PROPERTY_TYPE,
    '',
  );
  const editKeyField = useListingField(
    ListingResource.Property,
    PropertyField.EDIT_KEY,
    '',
  );
  const countyField = useListingField(
    ListingResource.Property,
    PropertyField.COUNTY,
    '',
  );

  const { media: allMedia } = useMediaResource();

  const {
    items: currentTours,
    setMediaCallback: updateMedia,
    displayItems,
  } = useSyncedMediaResource(MediaCategory.Tour);

  const { validateUploadSet } = useMediaValidation();
  const { deleteListingResources } = useService();
  const listingKey = listingKeyField.value;
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);

  const [tourName, setTourName] = useState<string>();
  const [tourURL, setTourURL] = useState<string>();
  const [hasURLError, setHasURLError] = useState(false);
  const [hasNameError, setHasNameError] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const { validateUrl } = useUrlValidation();

  const pendingUploadsRef = useRef(new Set<number>());

  const handleUploadComplete = useCallback(
    (result: any) => {
      const newMediaFile = result;
      pendingUploadsRef.current.delete(newMediaFile.MEDIA_ITEM_NUMBER);

      updateMedia((prevFiles) => {
        const newFiles = [...prevFiles];

        // Assign display order that ensures placement at the end
        newFiles.push(newMediaFile);

        return newFiles;
      });
    },
    [updateMedia],
  );

  const { uploadMediaBatch, isUploading, uploadProgress } = useMediaUpload({
    listingKey,
    listingDetails: {
      CWAPropertyType: propertyTypeField.value,
      CWALISKey: editKeyField.value,
      CWACounty: countyField.value,
    },
    logger,
    onUploadComplete: handleUploadComplete,
    onUploadError: (error, metadata) => {
      console.error(`Failed to upload ${metadata.name}:`, error);
    },
  });

  const handleAddTour = useCallback(async () => {
    if (!tourName) {
      setHasNameError(true);
      setErrorMessage('Please enter a tour name');
      setAnchorEl(document.getElementById('tour-name-field'));
      return;
    }

    if (!tourURL) {
      setHasURLError(true);
      setErrorMessage('Please enter a URL');
      setAnchorEl(document.getElementById('tour-url-field'));
      return;
    }

    const urlValidation = validateUrl(tourURL);
    if (!urlValidation.isValid) {
      setHasURLError(true);
      setErrorMessage(urlValidation.error || 'Invalid URL');
      setAnchorEl(document.getElementById('tour-url-field'));
      return;
    }

    try {
      // Create a virtual file for validation
      const virtualFile = new File([tourURL], tourName, { type: 'text/plain' });

      const validFiles = await validateUploadSet({
        files: [virtualFile],
        type: MediaType.Tour,
        currentCount: currentTours.length,
      });

      if (validFiles.length === 0) {
        return;
      }

      const maxItemNumber = Math.max(
        ...allMedia.map((item: any) => item.MEDIA_ITEM_NUMBER),
        ...currentTours.map((item: any) => item.MEDIA_ITEM_NUMBER),
        0,
      );

      const baseDisplayOrder =
        Math.max(...currentTours.map((f) => f.MEDIA_DISPLAY_ORDER), -1) + 1;

      const itemNumber = maxItemNumber + 1;
      pendingUploadsRef.current.add(itemNumber);

      const mediaItem: MediaMetadata = {
        name: tourURL,
        type: 'txt',
        category: MediaCategory.Tour,
        description: tourName,
        shortDescription: tourName,
        size: 0,
        itemNumber,
        sizeDescription: 'Virtual Tour - Standard',
        displayOrder: baseDisplayOrder,
      };

      await uploadMediaBatch([mediaItem]);

      setTourName('');
      setTourURL('');
      setHasNameError(false);
      setHasURLError(false);
    } catch (error) {
      console.error('Failed to add virtual tour:', error);
      setErrorMessage('Failed to add virtual tour');
      setAnchorEl(document.getElementById('add-tour-button'));
    }
  }, [
    allMedia,
    currentTours,
    pendingUploadsRef,
    setAnchorEl,
    setErrorMessage,
    setHasNameError,
    setHasURLError,
    setTourName,
    setTourURL,
    tourName,
    tourURL,
  ]);

  const handleBulkDelete = useCallback(async () => {
    setIsDeleteModalOpen(false);

    try {
      // Get all display orders of files being deleted
      const deletedDisplayOrders = new Set(
        currentTours
          .filter((file) => selectedFiles.includes(file.MED_VW_KEY))
          .map((file) => file.MEDIA_DISPLAY_ORDER),
      );

      // Delete the selected files from the backend
      await deleteListingResources(
        parseInt(listingKey || ''),
        selectedFiles.map((key) => ({
          type: 'Media',
          itemKey: parseInt(key),
        })),
      );

      // Filter out deleted files and adjust remaining display orders
      const remainingFiles = currentTours
        .filter((file) => !selectedFiles.includes(file.MED_VW_KEY))
        .map((file) => {
          // Count how many deleted files had lower display orders
          const lowerOrderedDeletions = Array.from(deletedDisplayOrders).filter(
            (order) => order < file.MEDIA_DISPLAY_ORDER,
          ).length;

          // Shift the display order down by the number of lower-ordered deletions
          return {
            ...file,
            MEDIA_DISPLAY_ORDER:
              file.MEDIA_DISPLAY_ORDER - lowerOrderedDeletions,
          };
        });

      // Update local state and media resource
      updateMedia(remainingFiles);

      // Clear the selection state
      setSelectedFiles([]);
    } catch (error) {
      console.error('Failed to delete selected tours:', error);
    } finally {
      setIsDeleteModalOpen(false);
    }
  }, [
    deleteListingResources,
    listingKey,
    currentTours,
    selectedFiles,
    updateMedia,
  ]);

  const onDeleteTour = useCallback(
    (fileVWKey: string) => {
      updateMedia((prevFiles) => {
        const updatedFiles = prevFiles
          .filter((file) => file.MED_VW_KEY !== fileVWKey)
          .map((file, index) => ({
            ...file,
            MEDIA_DISPLAY_ORDER: index,
          }));

        return updatedFiles;
      });

      setSelectedFiles((prevSelected) =>
        prevSelected.filter((selected) => selected !== fileVWKey),
      );

      deleteListingResources(parseInt(listingKey || ''), [
        {
          type: 'Media',
          itemKey: parseInt(fileVWKey),
        },
      ]).catch(console.error);
    },
    [deleteListingResources, listingKey, updateMedia, setSelectedFiles],
  );

  const handleMoveTourToFirst = useCallback(
    (fileVWKey: string) => {
      updateMedia((prevFiles) => {
        const targetTour = prevFiles.find(
          (tour) => tour.MED_VW_KEY === fileVWKey,
        );
        if (!targetTour) return prevFiles;

        const otherTours = prevFiles
          .filter((tour) => tour.MED_VW_KEY !== fileVWKey)
          .sort((a, b) => a.MEDIA_DISPLAY_ORDER - b.MEDIA_DISPLAY_ORDER);

        const updatedTours = [
          { ...targetTour, MEDIA_DISPLAY_ORDER: 0 },
          ...otherTours.map((tour, index) => ({
            ...tour,
            MEDIA_DISPLAY_ORDER: index + 1,
          })),
        ];

        return updatedTours;
      });
    },
    [updateMedia],
  );

  const handleClosePopover = () => {
    setAnchorEl(null);
    setErrorMessage('');
  };

  return (
    <>
      <Box
        sx={{
          backgroundColor: 'background.secondary',
          border: 'dashed 3px',
          borderColor: 'states.focus',
          borderRadius: 2,
          display: 'flex',
          justifyContent: 'center',
          padding: 2,
        }}
      >
        <Grid
          container
          spacing={2}
          sx={{
            width: 1,
            maxWidth: '1024px',
          }}
        >
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 4.5,
            }}
          >
            <Typography fontWeight="bold">Virtual Tour Name</Typography>
            <TextField
              data-testId={generateDataTestId(
                'Virtual Tour Name',
                undefined,
                'textField',
              )}
              id="tour-name-field"
              placeholder="Enter Virtual Tour Name"
              value={tourName}
              error={hasNameError}
              onChange={(e) => {
                setTourName(e.target.value);
                setHasNameError(false);
              }}
              fullWidth
              sx={{
                margin: { xs: '8px 0', sm: 'inherit' },
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 4.5,
            }}
          >
            <Typography fontWeight="bold">Virtual Tour URL</Typography>
            <TextField
              data-testId={generateDataTestId(
                'Virtual Tour URL',
                undefined,
                'urlField',
              )}
              id="tour-url-field"
              placeholder="Enter Virtual Tour URL"
              value={tourURL}
              error={hasURLError}
              onChange={(e) => {
                setTourURL(e.target.value);
                setHasURLError(false);
              }}
              fullWidth
              sx={{
                margin: { xs: '8px 0', sm: 'inherit' },
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 3,
            }}
            sx={{
              alignContent: 'end',
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: { xs: 'center', md: 'start' },
            }}
          >
            <Button
              data-testid="Add Virtual Tour-button"
              variant="contained"
              className="bmls_btn"
              sx={{
                alignSelf: { sm: 'center' },
                display: 'flex',
                height: { xs: '54px' },
                justifyContent: 'center',
                mb: '2px',
                width: { xs: 'auto', md: 1 },
              }}
              onClick={handleAddTour}
              color="secondary"
              disabled={isUploading}
            >
              <Typography fontWeight="bold">Add Virtual Tour</Typography>
            </Button>
          </Grid>
        </Grid>
      </Box>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Typography sx={{ p: 2, color: 'error.main' }}>
          {errorMessage}
        </Typography>
      </Popover>

      {displayItems.length === 0 && (
        <Box sx={{ textAlign: 'center', my: 16 }}>
          <Typography sx={{ fontWeight: 'bold' }}>
            You have no virtual tours
          </Typography>
          <Typography>
            Add virtual tours using the add tour tool above.
          </Typography>
        </Box>
      )}
      {displayItems.length !== 0 && (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: isSmallDisplay ? 'space-between' : 'flex-start',
              mt: 2,
            }}
          >
            <Button
              className="bmls_btn"
              onClick={() => {
                if (selectedFiles.length === currentTours.length) {
                  setSelectedFiles([]);
                } else {
                  setSelectedFiles(currentTours.map((f) => f.MED_VW_KEY));
                }
              }}
              sx={{
                maxWidth: { xs: '200px' },
                pl: 0,
                mx: { xs: 0, sm: 2 },
              }}
            >
              <Checkbox
                className="bmls_checkbox"
                size="medium"
                checked={
                  selectedFiles.length === currentTours.length &&
                  currentTours.length > 0
                }
                indeterminate={
                  selectedFiles.length > 0 &&
                  selectedFiles.length < currentTours.length
                }
              />
              <Typography sx={{ color: 'black' }}>Select All</Typography>
            </Button>

            <Button
              disabled={selectedFiles.length === 0}
              onClick={() => setIsDeleteModalOpen(true)}
              sx={{
                maxWidth: { xs: '200px' },
                pl: 0,
                color: selectedFiles.length > 0 ? 'error.medium' : 'grey.400',
              }}
              className="bmls_btn"
              startIcon={
                <DeleteIcon
                  sx={{
                    color:
                      selectedFiles.length > 0 ? 'error.medium' : 'grey.400',
                  }}
                />
              }
            >
              <Typography
                sx={{
                  color: selectedFiles.length > 0 ? 'error.medium' : 'grey.400',
                }}
              >
                {selectedFiles.length === 0
                  ? 'Delete Selected'
                  : `Delete Selected (${selectedFiles.length})`}
              </Typography>
            </Button>
          </Box>
          <Grid
            container
            spacing={2}
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              mb: { xs: 4, md: 6 },
              width: '100%',
            }}
          >
            {displayItems.map((item: any, _index: any) => {
              return (
                <Grid
                  key={item.MED_VW_KEY}
                  size={{
                    xs: 12,
                    md: 6,
                    lg: 4,
                  }}
                >
                  <TourTile
                    id={item.MEDIA_DISPLAY_ORDER}
                    file={item}
                    onDeleteTour={onDeleteTour}
                    moveTourToFirst={handleMoveTourToFirst}
                  />
                </Grid>
              );
            })}
          </Grid>
        </>
      )}

      <UploadDialog
        open={isUploading}
        hidePreview={true}
        uploadProgress={uploadProgress}
      />
      <BulkDeleteConfirmModal
        mediaType="Tours"
        numberSelectedMedia={selectedFiles.length}
        isDeleteModalOpen={isDeleteModalOpen}
        setIsDeleteModalOpen={setIsDeleteModalOpen}
        onDeletePhotos={handleBulkDelete}
      />
    </>
  );
};

export default AddVirtualTour;
